import {
  Activity,
  ArrowRightLeft,
  Banknote,
  BarChart3,
  Bot,
  Box,
  Briefcase,
  Building2,
  Calculator,
  Calendar,
  Clock3,
  CreditCard,
  Database,
  DollarSign,
  Factory,
  FileText,
  FlaskConical,
  GitBranch,
  History,
  Landmark,
  LayoutDashboard,
  Layers,
  MapPin,
  MonitorPlay,
  Package,
  Percent,
  PieChart,
  PiggyBank,
  Printer,
  RefreshCcw,
  ScanLine,
  Settings,
  ShoppingBag,
  ShoppingCart,
  Store,
  Tag,
  Ticket,
  TrendingDown,
  TrendingUp,
  Upload,
  UserCheck,
  Users,
  Wallet,
  Zap,
} from 'lucide-react';

export const academiaCategorias = [
  {
    titulo: '1. Inicio y Soporte',
    modulos: [
      {
        id: 'dashboard',
        title: 'Dashboard General',
        description:
          'Pantalla de entrada para revisar indicadores clave del negocio y detectar rápido ventas, stock, cobranzas o pendientes operativos.',
        icon: LayoutDashboard,
        pasos: [
          'Al ingresar al sistema, usa las tarjetas superiores para leer el estado general del día.',
          'Revisa los gráficos y comparativos por período para entender la evolución de ventas y actividad.',
          'Usa los accesos rápidos del tablero para saltar al módulo que necesita atención inmediata.',
          'Toma el dashboard como punto de control antes de operar y también al cierre de la jornada.'
        ]
      },
      {
        id: 'chatbot',
        requiredModule: 'chatbot',
        title: 'IA Asistente',
        description:
          'Asistente operativo pensado para ayudarte a encontrar el paso a paso de Gastify usando como base los manuales del sistema.',
        icon: Bot,
        pasos: [
          'Abre el asistente desde la barra superior cuando necesites ayuda sobre un módulo o proceso.',
          'Haz preguntas concretas, por ejemplo sobre facturación, compras, inventario o tesorería.',
          'Úsalo para ubicar rutas del sistema y confirmar el orden correcto de cada operación.',
          'Ten en cuenta que responde sobre uso funcional del sistema, no sobre datos privados de tu empresa.'
        ]
      }
    ]
  },
  {
    titulo: '2. Ventas y Facturación',
    modulos: [
      {
        id: 'ventas-pos',
        requiredModule: 'ventas-pos',
        title: 'Punto de Venta (POS)',
        description:
          'Interfaz de venta rápida para mostrador o tablet, con carrito táctil, descuentos o recargos y cobro inmediato.',
        icon: MonitorPlay,
        pasos: [
          'Ingresa a Ventas → Punto de Venta y busca productos por nombre, código o escaneo.',
          'Filtra por categoría y agrega artículos al carrito desde el catálogo.',
          'Ajusta cantidades y, si corresponde, aplica descuento o recargo porcentual.',
          'Selecciona el medio de pago y confirma con Cobrar o usa el modo presupuesto si no quieres impactar stock ni caja.'
        ]
      },
      {
        id: 'ventas-profesional',
        requiredModule: 'ventas-profesional',
        title: 'Ventas Profesional',
        description:
          'Pantalla de venta completa para trabajar con cliente, condición de pago, lista de precios y documentos más detallados.',
        icon: Briefcase,
        pasos: [
          'Entra en Ventas → Ventas Profesional y selecciona el cliente o créalo desde el botón de alta.',
          'Define la condición de pago y la lista de precios que corresponden a la operación.',
          'Carga productos, cantidades y precios, con opción de descuentos por ítem o globales.',
          'Selecciona el medio de pago y registra la venta para emitir remito, factura o presupuesto según el caso.'
        ]
      },
      {
        id: 'ventas-historial',
        requiredModule: 'ventas-historial',
        title: 'Historial de Ventas',
        description:
          'Consulta central de ventas registradas para filtrar, auditar comprobantes y ejecutar acciones sobre cada operación.',
        icon: History,
        pasos: [
          'Abre Ventas → Historial y aplica filtros por fechas, estado, cliente o sucursal.',
          'Ingresa al detalle de una venta para revisar ítems, pagos y datos del comprobante.',
          'Desde el listado puedes reimprimir, anular o enviar una venta a facturación fiscal.',
          'Usa este historial como referencia para seguimiento comercial y control administrativo.'
        ]
      },
      {
        id: 'facturacion',
        requiredModule: 'facturacion',
        title: 'Facturación (ARCA)',
        description:
          'Emisión y gestión de comprobantes fiscales electrónicos conectados con ARCA, incluyendo facturas y notas de crédito.',
        icon: FileText,
        pasos: [
          'Ve a Ventas → Facturación y selecciona las ventas pendientes de facturar.',
          'Revisa cliente, tipo de comprobante y datos fiscales antes de confirmar.',
          'Haz clic en Facturar para que el sistema solicite el CAE a ARCA y registre el comprobante.',
          'Una vez aprobado, imprime o descarga la factura. Antes de usarlo, configura certificados y punto de venta en Configuración.'
        ]
      },
      {
        id: 'presupuestos',
        requiredModule: 'presupuestos',
        title: 'Presupuestos',
        description:
          'Herramienta para generar cotizaciones formales, con vencimiento definido y opción de convertirlas luego en venta.',
        icon: Calculator,
        pasos: [
          'Ingresa a Ventas → Presupuestos o usa el modo Presupuesto desde el POS.',
          'Selecciona el cliente y carga los ítems con cantidades y precios.',
          'Define la fecha de validez y guarda o imprime el presupuesto.',
          'Cuando el cliente aprueba, conviértelo en venta para copiar ítems y condiciones e impactar stock.'
        ]
      },
      {
        id: 'listas-precios',
        requiredModule: 'listas-precios',
        title: 'Listas de Precios',
        description:
          'Precios diferenciados por segmento, canal o campaña, aplicables automáticamente al momento de vender.',
        icon: Percent,
        pasos: [
          'Crea una nueva lista desde Ventas → Listas de Precios.',
          'Asigna un nombre descriptivo y define precios específicos o porcentajes de ajuste sobre la base.',
          'Guarda la lista y mantenla actualizada según tu estrategia comercial.',
          'Al vender desde Ventas Profesional, elige la lista para que los precios se completen automáticamente.'
        ]
      },
      {
        id: 'clientes',
        requiredModule: 'clientes',
        title: 'Clientes',
        description:
          'Padrón comercial y fiscal de clientes con historial de operaciones, cuenta corriente y condiciones de venta asociadas.',
        icon: Users,
        pasos: [
          'Da de alta un cliente desde Ventas → Clientes → Nuevo Cliente.',
          'Completa nombre o razón social, CUIT o DNI, contacto y dirección.',
          'Asigna la condición de pago y la lista de precios por defecto si corresponde.',
          'Consulta luego su ficha para ver historial, saldo en cuenta corriente y acceso rápido a cobranzas.'
        ]
      }
    ]
  },
  {
    titulo: '3. Compras y Abastecimiento',
    modulos: [
      {
        id: 'compras-ocr',
        requiredModule: 'compras-ocr',
        title: 'Escáner de Compras (OCR)',
        description:
          'Carga facturas de proveedor desde imagen o PDF para extraer automáticamente datos, ítems y totales.',
        icon: ScanLine,
        pasos: [
          'Ingresa a Compras → Escáner Compras y sube una foto o PDF de la factura.',
          'Espera a que el sistema extraiga proveedor, fecha, artículos, precios y total.',
          'Revisa y corrige la información detectada antes de confirmar.',
          'Confirma la compra para crear el registro e impactar stock. Funciona mejor con imágenes nítidas.'
        ]
      },
      {
        id: 'compras-nueva',
        requiredModule: 'compras-nueva',
        title: 'Nueva Compra',
        description:
          'Registro manual de compras a proveedor con actualización de stock y generación de deuda o egreso según la condición de pago.',
        icon: ShoppingCart,
        pasos: [
          'Ve a Compras → Nueva Compra y selecciona el proveedor.',
          'Carga fecha, número de factura o remito y los ítems comprados con cantidades y precio unitario.',
          'Indica si la operación es contado o crédito antes de confirmar.',
          'Al guardar, el sistema suma stock y registra deuda al proveedor o egreso bancario según corresponda.'
        ]
      },
      {
        id: 'pedido-sugerido',
        requiredModule: 'pedido-sugerido',
        title: 'Pedido Sugerido',
        description:
          'Reposición asistida basada en stock mínimo para detectar faltantes y armar rápidamente una orden de compra.',
        icon: Layers,
        pasos: [
          'Abre Compras → Pedido Sugerido para analizar productos con stock igual o menor al mínimo.',
          'Revisa la lista propuesta y ajusta cantidades si hace falta.',
          'Selecciona un proveedor por ítem o uno global para toda la reposición.',
          'Confirma para generar la orden de compra. El resultado depende de tener bien configurado el stock mínimo en productos.'
        ]
      },
      {
        id: 'compras',
        requiredModule: 'compras',
        title: 'Historial de Compras',
        description:
          'Listado de compras registradas con filtros por fechas, proveedor y estado de pago, pensado para consulta y auditoría.',
        icon: History,
        pasos: [
          'Ingresa a Compras → Historial y filtra por rango de fechas, proveedor o estado.',
          'Abre una compra para revisar ítems, importes, cuenta corriente y comprobante asociado.',
          'Si hace falta, anula una compra para revertir stock y deuda con el proveedor.',
          'Usa el acceso rápido a Registrar pago para pasar directo al circuito de tesorería.'
        ]
      },
      {
        id: 'proveedores',
        requiredModule: 'proveedores',
        title: 'Proveedores',
        description:
          'Padrón completo de proveedores con datos fiscales, historial de compras y seguimiento de cuenta corriente.',
        icon: Building2,
        pasos: [
          'Crea un proveedor desde Compras → Proveedores → Nuevo Proveedor.',
          'Completa razón social, CUIT, contacto, dirección y condición fiscal.',
          'Define la condición de pago por defecto para agilizar nuevas compras.',
          'Consulta su ficha para ver historial, saldo pendiente y acceso directo a pagos de tesorería.'
        ]
      }
    ]
  },
  {
    titulo: '4. Inventario y Productos',
    modulos: [
      {
        id: 'inventario',
        requiredModule: 'inventario',
        title: 'Stock Actual',
        description:
          'Vista en tiempo real del inventario, con stock total, desglose por sucursal y alertas de reposición.',
        icon: Package,
        pasos: [
          'Entra a Inventario para revisar existencias por nombre, código o categoría.',
          'Filtra por sucursal si necesitas ver una sede específica.',
          'Usa los estados de stock para detectar bajo stock, sin stock o inventario normal.',
          'Toma esta grilla como base para reposición, transferencias y control diario.'
        ]
      },
      {
        id: 'productos',
        requiredModule: 'productos',
        title: 'Productos',
        description:
          'Catálogo principal de artículos con precios, costos, categorías, unidades, stock y datos complementarios.',
        icon: Box,
        pasos: [
          'Crea un artículo desde Inventario → Productos → Nuevo Producto.',
          'Completa nombre, código o SKU, categoría, unidad de medida y valores comerciales.',
          'Configura stock inicial, stock mínimo y si el producto lleva control de stock.',
          'Desde la ficha podrás editar datos, revisar movimientos y activar o desactivar el artículo.'
        ]
      },
      {
        id: 'inventario-transferencia',
        requiredModule: 'inventario',
        title: 'Transferencias de Stock',
        description:
          'Movimiento controlado de mercadería entre sucursales con trazabilidad completa de origen, destino y usuario.',
        icon: RefreshCcw,
        pasos: [
          'Ve a Inventario → Transferencias → Nueva Transferencia.',
          'Selecciona la sucursal de origen y la de destino.',
          'Agrega productos, cantidades y observaciones si hacen falta.',
          'Confirma para descontar stock en origen y sumarlo en destino, dejando el movimiento registrado.'
        ]
      },
      {
        id: 'inventario-ajuste',
        requiredModule: 'inventario',
        title: 'Ajustes de Stock',
        description:
          'Corrección de diferencias entre stock físico y stock del sistema por merma, rotura, robo o conteo.',
        icon: Settings,
        pasos: [
          'Ingresa a Inventario → Ajustes de Stock y elige la sucursal.',
          'Agrega el producto a corregir e indica la cantidad positiva o negativa.',
          'Selecciona el motivo del ajuste y registra observaciones.',
          'Confirma para dejar el cambio asentado en el historial del producto con usuario y fecha.'
        ]
      },
      {
        id: 'inventario-etiquetas',
        requiredModule: 'inventario',
        title: 'Imprimir Etiquetas',
        description:
          'Generación de etiquetas de precio y código de barras listas para impresoras térmicas o formatos estándar.',
        icon: Tag,
        pasos: [
          'Abre Inventario → Imprimir Etiquetas y selecciona los productos.',
          'Indica cuántas etiquetas quieres por artículo.',
          'Elige el formato y la información que debe mostrar cada etiqueta.',
          'Genera o imprime el lote con nombre, precio, código de barras y logo si está configurado.'
        ]
      }
    ]
  },
  {
    titulo: '5. Producción',
    modulos: [
      {
        id: 'recetas',
        requiredModule: 'produccion-registrar',
        title: 'Recetas',
        description:
          'Definición de fórmulas productivas para indicar insumos, cantidades y rendimiento de cada producto elaborado.',
        icon: FlaskConical,
        pasos: [
          'Ve a Producción → Recetas → Nueva Receta.',
          'Selecciona el producto terminado y define cuántas unidades produce una ejecución.',
          'Agrega los insumos y la cantidad necesaria por receta.',
          'Guarda la fórmula. Los cambios que hagas luego aplicarán a producciones futuras.'
        ]
      },
      {
        id: 'produccion-registrar',
        requiredModule: 'produccion-registrar',
        title: 'Registrar Producción',
        description:
          'Carga de lotes producidos descontando insumos y sumando producto terminado en la sucursal correspondiente.',
        icon: Factory,
        pasos: [
          'Ingresa a Producción → Registrar Producción y elige un producto con receta activa.',
          'Indica la cantidad de lotes a elaborar para que el sistema calcule insumos a consumir.',
          'Selecciona la sucursal, agrega observaciones o número de lote si hace falta.',
          'Confirma para descontar insumos, sumar producto terminado y registrar el lote en historial.'
        ]
      },
      {
        id: 'pedidos-produccion',
        requiredModule: 'pedidos-produccion',
        title: 'Pedidos de Producción',
        description:
          'Órdenes de elaboración generadas desde ventas o de forma manual para coordinar qué debe producirse y en qué estado está.',
        icon: GitBranch,
        pasos: [
          'Abre Pedidos de Producción para ver órdenes Pendientes, En proceso, Completadas o Canceladas.',
          'Selecciona un pedido para revisar ítems y cantidades requeridas.',
          'Usa la acción Producir para avanzar la fabricación del pedido.',
          'Cuando la elaboración termina, marca el pedido como completado para liberar el siguiente paso operativo.'
        ]
      },
      {
        id: 'produccion-historial',
        requiredModule: 'produccion-historial',
        title: 'Historial de Producción',
        description:
          'Registro cronológico de lotes elaborados con detalle de insumos consumidos, usuario, fecha y costo.',
        icon: History,
        pasos: [
          'Consulta el historial para buscar lotes por producto, fecha, sucursal o número de lote.',
          'Ingresa al detalle de un lote para revisar cantidades producidas e insumos utilizados.',
          'Verifica sucursal, usuario y momento exacto del registro cuando necesites auditar producción.',
          'Usa esta información para análisis operativos, costos y seguimiento de trazabilidad.'
        ]
      },
      {
        id: 'produccion-distribucion',
        requiredModule: 'produccion-distribucion',
        title: 'Distribución de Costos',
        description:
          'Análisis del costo de producción por producto elaborado, con desglose por insumo y comparación contra precio de venta.',
        icon: PieChart,
        pasos: [
          'Selecciona un producto elaborado dentro de Distribución de Costos.',
          'Revisa cuánto aporta cada insumo al costo total de producción.',
          'Consulta el costo unitario actualizado y el margen frente al precio de venta.',
          'Usa esta vista para ajustar fórmulas, precios o decisiones de abastecimiento.'
        ]
      }
    ]
  },
  {
    titulo: '6. Logística',
    modulos: [
      {
        id: 'logistica-pedidos',
        requiredModule: 'logistica',
        title: 'Pedidos Logísticos',
        description:
          'Listado de pedidos a preparar y despachar, provenientes de ventas o de la tienda online.',
        icon: ShoppingBag,
        pasos: [
          'Abre Logística para ver pedidos recibidos, en preparación, listos, en camino o entregados.',
          'Selecciona un pedido para revisar ítems y dirección de entrega.',
          'Actualiza el estado según el avance real del proceso.',
          'Asigna repartidor o transportista y deja observaciones de entrega cuando haga falta.'
        ]
      },
      {
        id: 'logistica-panel-operativo',
        requiredModule: 'logistica',
        title: 'Panel Operativo',
        description:
          'Vista de trabajo para el equipo de logística con pedidos agrupados por estado y herramientas de seguimiento rápido.',
        icon: Layers,
        pasos: [
          'Trabaja el panel en formato columnas o lista para ordenar la operación diaria.',
          'Mueve pedidos entre estados con un clic para reflejar el flujo real.',
          'Filtra por sucursal o repartidor para repartir la carga de trabajo.',
          'Imprime hojas de ruta cuando necesites organizar salidas o recorridos.'
        ]
      },
      {
        id: 'logistica-entregas',
        requiredModule: 'logistica',
        title: 'Entregas',
        description:
          'Registro detallado de entregas realizadas, con receptor, fecha, hora y novedades del despacho.',
        icon: MapPin,
        pasos: [
          'Toma un pedido en estado listo para envío y usa Registrar Entrega.',
          'Confirma receptor, fecha y hora previstas o efectivas.',
          'Carga observaciones si hubo ausente, daños o cualquier novedad.',
          'Al confirmar, el pedido pasa a entregado y queda disponible en el historial para control o liquidación.'
        ]
      },
      {
        id: 'logistica-flujos',
        requiredModule: 'logistica',
        title: 'Flujos con Producción',
        description:
          'Seguimiento de pedidos que requieren fabricación previa, integrando producción y despacho en un mismo circuito.',
        icon: GitBranch,
        pasos: [
          'Cuando un pedido incluye elaborados, el sistema puede generar el pedido de producción asociado.',
          'Producción fabrica los ítems y actualiza el estado operativo del pedido.',
          'Una vez terminada la elaboración, logística recibe el pedido como listo para envío.',
          'Usa esta trazabilidad para que ambos equipos trabajen en paralelo con visibilidad compartida.'
        ]
      }
    ]
  },
  {
    titulo: '7. Finanzas y Tesorería',
    modulos: [
      {
        id: 'bancos',
        requiredModule: 'bancos',
        title: 'Bancos',
        description:
          'Centralización de cuentas bancarias y billeteras digitales, con saldos actualizados y detalle completo de movimientos.',
        icon: Landmark,
        pasos: [
          'Crea una cuenta desde Nuevo Banco indicando nombre, CBU o alias, moneda y saldo inicial.',
          'Ingresa al detalle para filtrar movimientos por fecha y revisar saldo resultante.',
          'Ordena y audita ingresos y egresos desde la grilla de movimientos.',
          'Usa Transferencias entre Cuentas para mover fondos sin perder trazabilidad.'
        ]
      },
      {
        id: 'tesoreria-cobranzas',
        requiredModule: 'tesoreria-cobranzas',
        title: 'Cobros',
        description:
          'Registro de cobranzas a clientes con cuenta corriente, acreditando el importe en la cuenta bancaria elegida.',
        icon: Wallet,
        pasos: [
          'Ve a Tesorería → Cobros y selecciona el cliente.',
          'Indica la cuenta de destino, la sucursal, el monto y la fecha.',
          'Confirma para acreditar el importe y registrar el movimiento en el libro diario.',
          'Si necesitas auditar o revertir, usa el historial y el filtro Mostrar eliminados.'
        ]
      },
      {
        id: 'tesoreria-pagos',
        requiredModule: 'tesoreria-pagos',
        title: 'Pagos a Proveedores',
        description:
          'Egresos destinados a cancelar deudas con proveedores, descontando automáticamente fondos de la cuenta elegida.',
        icon: Banknote,
        pasos: [
          'Ingresa a Tesorería → Pagos y elige el proveedor.',
          'Selecciona la cuenta bancaria de origen y carga monto, fecha y observaciones.',
          'Confirma para registrar el egreso como pago a proveedor.',
          'Si hace falta corregir, anula el pago desde el historial expandiendo el registro.'
        ]
      },
      {
        id: 'tesoreria-transferencia',
        requiredModule: 'tesoreria-transferencia',
        title: 'Transferencias Internas',
        description:
          'Movimiento de fondos entre cuentas propias, dejando un egreso en origen y un ingreso en destino.',
        icon: ArrowRightLeft,
        pasos: [
          'Desde Tesorería o Bancos, abre la pantalla de Transferencias.',
          'Selecciona cuenta de origen y destino, que no pueden ser iguales.',
          'Indica monto, fecha y observaciones.',
          'Confirma para registrar ambos movimientos y mantener actualizados los saldos.'
        ]
      },
      {
        id: 'gastos',
        requiredModule: 'gastos',
        title: 'Gastos Operativos',
        description:
          'Registro de egresos operativos por sucursal, con impacto directo en la cuenta bancaria seleccionada.',
        icon: TrendingDown,
        pasos: [
          'Completa concepto o categoría, monto, fecha, sucursal y cuenta bancaria.',
          'Agrega observaciones como número de factura u otro detalle de respaldo.',
          'Confirma el gasto para que el sistema lo registre como egreso operativo.',
          'Si hace falta editar o anular, hazlo desde la tabla de auditoría del módulo.'
        ]
      },
      {
        id: 'caja',
        requiredModule: 'caja',
        title: 'Arqueo de Caja',
        description:
          'Gestión del turno de caja: apertura, movimientos manuales durante la jornada y cierre con comparación teórico vs real.',
        icon: PiggyBank,
        pasos: [
          'Abre caja ingresando el saldo inicial en efectivo y cualquier observación de apertura.',
          'Durante el turno registra ingresos, gastos, retiros o egresos manuales cuando corresponda.',
          'Al cerrar, informa el monto real contado por cada método de pago.',
          'Revisa la diferencia entre teórico y real antes de finalizar el turno.'
        ]
      },
      {
        id: 'conciliacion-bancaria',
        requiredModule: 'conciliacion-bancaria',
        title: 'Conciliación Bancaria',
        description:
          'Comparación entre movimientos del sistema y extracto real del banco para detectar diferencias y dejar trazabilidad.',
        icon: RefreshCcw,
        pasos: [
          'Selecciona la cuenta bancaria y define el rango de fechas a conciliar.',
          'Importa el CSV del banco con fecha, descripción, referencia y monto.',
          'Compara los movimientos y marca como conciliados los que coincidan.',
          'Investiga o corrige los registros que queden pendientes para cerrar la conciliación.'
        ]
      },
      {
        id: 'conciliacion-ctas-ctes',
        requiredModule: 'conciliacion-ctas-ctes',
        title: 'Conciliación de Cuentas Corrientes',
        description:
          'Imputación de cobros y pagos a documentos específicos para saldar ventas o compras pendientes.',
        icon: ArrowRightLeft,
        pasos: [
          'Elige si vas a trabajar con clientes o proveedores.',
          'Selecciona el movimiento de cobro o pago y la entidad correspondiente.',
          'Revisa los documentos pendientes que el sistema propone para imputar.',
          'Asigna montos y guarda para dejar facturas pagadas total o parcialmente.'
        ]
      },
      {
        id: 'flujo-fondos',
        requiredModule: 'flujo-fondos',
        title: 'Flujo de Fondos',
        description:
          'Control de liquidez que combina movimientos reales con proyecciones futuras en vistas calendario, análisis e historial.',
        icon: Activity,
        pasos: [
          'Usa la vista Calendario para ver el flujo neto diario del mes.',
          'Consulta Análisis para desglosar ingresos y egresos por categoría o entidad.',
          'Carga ingresos o gastos proyectados para anticiparte a faltantes o excedentes.',
          'Confirma, pospone o elimina proyecciones según se conviertan en movimientos reales.'
        ]
      }
    ]
  },
  {
    titulo: '8. Tienda Virtual',
    modulos: [
      {
        id: 'pedidos-web',
        requiredModule: 'pedidos-web',
        title: 'Pedidos Web',
        description:
          'Gestión de órdenes recibidas desde la tienda online, con notificaciones automáticas y seguimiento del estado.',
        icon: ShoppingBag,
        pasos: [
          'Abre Tienda Virtual → Pedidos Web para revisar los pedidos nuevos.',
          'Ingresa al detalle para validar ítems, cliente y modalidad de envío o retiro.',
          'Cambia el estado según avance la preparación, despacho o entrega.',
          'Usa impresión o hoja de ruta cuando necesites coordinar el envío con logística.'
        ]
      },
      {
        id: 'tienda-config',
        requiredModule: 'tienda-config',
        title: 'Configuración de Tienda',
        description:
          'Personalización del catálogo online, apariencia, métodos de pago, envíos y alertas del canal digital.',
        icon: Store,
        pasos: [
          'Define qué productos y categorías se muestran en el catálogo online.',
          'Carga imágenes, banner principal, logo, colores y descripción del negocio.',
          'Configura opciones de envío, retiro y métodos de pago aceptados.',
          'Activa o desactiva sonido, toast y notificaciones push para nuevos pedidos.'
        ]
      }
    ]
  },
  {
    titulo: '9. CRM y Seguimiento',
    modulos: [
      {
        id: 'crm-dashboard',
        requiredModule: 'crm-dashboard',
        title: 'Mi Panel CRM',
        description:
          'Vista personal del usuario con tickets asignados, tareas del día, horas trabajadas, proyectos y próximas reuniones.',
        icon: Activity,
        pasos: [
          'Ingresa a Mi Panel para revisar la carga operativa individual.',
          'Ordena tickets por prioridad para atender primero lo más urgente.',
          'Controla horas registradas y tareas pendientes del día.',
          'Usa el panel como punto de entrada antes de pasar a tickets, proyectos o reuniones.'
        ]
      },
      {
        id: 'crm-tickets',
        requiredModule: 'crm-tickets',
        title: 'Tickets',
        description:
          'Gestión de solicitudes de soporte, servicio o tareas para clientes, con seguimiento completo de estado y actividad.',
        icon: Ticket,
        pasos: [
          'Crea un ticket desde Seguimiento de Tickets → Tickets → Nuevo Ticket.',
          'Completa título, descripción, cliente, prioridad, tipo y responsable.',
          'Dentro del detalle puedes cambiar estado, agregar comentarios y adjuntar archivos.',
          'Vincula horas trabajadas, subtareas o proyectos para centralizar toda la gestión.'
        ]
      },
      {
        id: 'crm-triaje',
        requiredModule: 'crm-triaje',
        title: 'Triaje de Tickets',
        description:
          'Vista rápida para priorizar y asignar tickets entrantes, ideal para distribuir la carga de trabajo del equipo.',
        icon: Zap,
        pasos: [
          'Revisa tickets sin asignar o recién creados en una sola bandeja.',
          'Asigna responsable con un clic según disponibilidad o especialidad.',
          'Ajusta prioridad y estado en lote cuando sea necesario.',
          'Filtra por cliente, tipo o prioridad para ordenar la operación de soporte.'
        ]
      },
      {
        id: 'crm-proyectos',
        requiredModule: 'crm-proyectos',
        title: 'Proyectos',
        description:
          'Agrupación de tickets y tareas bajo un objetivo común, con control de avance, equipo participante e historial.',
        icon: Briefcase,
        pasos: [
          'Crea un proyecto con nombre, cliente, descripción y fechas estimadas.',
          'Asigna los miembros del equipo que van a trabajar en él.',
          'Desde el detalle consulta avance, tickets asociados y horas por integrante.',
          'Usa el historial del proyecto para seguir cambios y evolución del trabajo.'
        ]
      },
      {
        id: 'crm-banco-horas',
        requiredModule: 'crm-banco-horas',
        title: 'Banco de Horas',
        description:
          'Registro del tiempo trabajado por usuario, proyecto o cliente para control interno y facturación de servicios.',
        icon: Clock3,
        pasos: [
          'Desde un ticket o tarea, usa Registrar Horas para cargar fecha, tiempo y descripción.',
          'Relaciona cada registro con el trabajo efectivamente realizado.',
          'Consulta reportes por usuario, cliente, proyecto o período.',
          'Aprovecha esta base para alimentar acuerdos de facturación por tiempo trabajado.'
        ]
      },
      {
        id: 'agenda',
        requiredModule: 'agenda',
        title: 'Reuniones',
        description:
          'Agenda de reuniones con clientes para ordenar compromisos, seguimientos y próximas acciones del equipo.',
        icon: Calendar,
        pasos: [
          'Registra reuniones vinculadas a clientes, tickets o proyectos.',
          'Usa la agenda para visualizar próximos encuentros y compromisos pendientes.',
          'Coordina fechas con el resto del equipo para evitar superposiciones.',
          'Mantén este módulo alineado con tickets y proyectos para conservar contexto operativo.'
        ]
      },
      {
        id: 'crm-acuerdos-facturacion',
        requiredModule: 'crm-acuerdos-facturacion',
        title: 'Acuerdos y Facturación',
        description:
          'Gestión de contratos de servicio, tarifas, horas incluidas y generación de facturación según el tipo de acuerdo.',
        icon: DollarSign,
        pasos: [
          'Define si el acuerdo será por tiempo y materiales, precio fijo o retainer.',
          'Configura tarifas, alcance y horas incluidas según el servicio pactado.',
          'Al cierre de período, revisa lo que corresponde facturar según horas o monto acordado.',
          'Confirma la operación para derivar la emisión al módulo de Facturación.'
        ]
      },
      {
        id: 'crm-reportes',
        requiredModule: 'crm-reportes',
        title: 'Reportes CRM',
        description:
          'Métricas operativas del equipo para analizar carga de trabajo, tiempos y desempeño por cliente o proyecto.',
        icon: BarChart3,
        pasos: [
          'Consulta indicadores de tickets, tiempos y horas registradas por período.',
          'Filtra por cliente, proyecto o responsable para encontrar cuellos de botella.',
          'Cruza esta información con banco de horas y acuerdos comerciales.',
          'Usa los reportes para planificación, seguimiento de SLA y cierre mensual.'
        ]
      }
    ]
  },
  {
    titulo: '10. Análisis y Reportes',
    modulos: [
      {
        id: 'gestion-costos',
        requiredModule: 'gestion-costos',
        title: 'Gestión de Costos',
        description:
          'Análisis y actualización de costos de productos, con margen calculado y herramientas para edición masiva.',
        icon: BarChart3,
        pasos: [
          'Consulta la grilla principal para ver costo actual, precio de venta y margen de cada producto.',
          'Edita un costo individual cuando necesites corregir un artículo puntual.',
          'Usa la actualización masiva para aplicar aumentos por categoría o sobre todo el catálogo.',
          'Si el proveedor envía planillas, importa costos desde CSV para acelerar la actualización.'
        ]
      },
      {
        id: 'rentabilidad-ventas',
        requiredModule: 'rentabilidad-ventas',
        title: 'Rentabilidad',
        description:
          'Análisis de márgenes por período, sucursal, categoría o vendedor para detectar qué vende mejor y qué rinde más.',
        icon: TrendingUp,
        pasos: [
          'Filtra por día, semana, mes, año o rango personalizado.',
          'Acota por sucursal, categoría o vendedor según el análisis que necesites.',
          'Revisa métricas como ventas totales, costo de ventas, margen bruto y ticket promedio.',
          'Usa el ranking de productos para detectar artículos más rentables o con pérdida.'
        ]
      },
      {
        id: 'reportes-ctas-ctes',
        requiredModule: 'reportes-ctas-ctes',
        title: 'Cuentas Corrientes',
        description:
          'Vista consolidada de saldos pendientes de clientes y proveedores para seguimiento financiero y exportación.',
        icon: Wallet,
        pasos: [
          'Consulta saldos pendientes de clientes con última compra y antigüedad de deuda.',
          'Revisa también los saldos a pagar a proveedores con la misma lógica de control.',
          'Ordena y filtra el listado para preparar cobranzas, pagos o reuniones administrativas.',
          'Exporta el informe a Excel cuando necesites compartirlo con administración o estudio contable.'
        ]
      },
      {
        id: 'reportes-impositivos',
        requiredModule: 'reportes-impositivos',
        title: 'Reportes Impositivos',
        description:
          'Compilación de IVA ventas, IVA compras, retenciones y percepciones para preparar declaraciones fiscales.',
        icon: FileText,
        pasos: [
          'Ingresa a Análisis → Reportes Impositivos y elige el tipo de reporte.',
          'Define período, normalmente por mes y año.',
          'Genera el informe para que el sistema compile datos de ventas y compras.',
          'Exporta en Excel o PDF y verifica que los comprobantes estén bien registrados para asegurar exactitud.'
        ]
      }
    ]
  },
  {
    titulo: '11. Configuración General',
    modulos: [
      {
        id: 'empresa-datos',
        requiredModule: 'config-sistema-datos',
        title: 'Mi Empresa',
        description:
          'Datos fiscales y de contacto de la organización que se usan en comprobantes, impresiones y documentación formal.',
        icon: Building2,
        pasos: [
          'Completa razón social, CUIT, condición IVA, dirección y datos de contacto.',
          'Carga el logo corporativo que se verá en comprobantes e impresiones.',
          'Revisa que CUIT y condición fiscal sean correctos antes de habilitar facturación.',
          'Usa esta pantalla como base maestra de identidad y datos legales del negocio.'
        ]
      },
      {
        id: 'config-facturacion',
        requiredModule: 'config-facturacion',
        title: 'Configuración de Facturación',
        description:
          'Parámetros para conectar Gastify con ARCA y emitir comprobantes fiscales electrónicos.',
        icon: FileText,
        pasos: [
          'Ingresa CUIT del emisor, punto de venta y tipos de comprobante habilitados.',
          'Sube el certificado digital y la clave privada provistos por ARCA.',
          'Elige si vas a trabajar en homologación o producción.',
          'Usa Probar Conexión antes de guardar para validar que todo esté configurado correctamente.'
        ]
      },
      {
        id: 'metodos-cobro',
        requiredModule: 'metodos-cobro',
        title: 'Métodos de Cobro',
        description:
          'Alta y mantenimiento de medios de pago como efectivo, tarjetas, transferencias y billeteras virtuales.',
        icon: CreditCard,
        pasos: [
          'Crea un método desde Configuración → Métodos de Cobro.',
          'Indica nombre, tipo y si corresponde un recargo o descuento porcentual.',
          'Asócialo a una cuenta bancaria para el impacto automático en saldos.',
          'Mantén activos solo los medios que deben aparecer en POS y cobranzas.'
        ]
      },
      {
        id: 'sitios',
        requiredModule: 'config-sistema-datos',
        title: 'Sucursales y Sitios',
        description:
          'Configuración de sedes, depósitos y puntos de venta usados en toda la operación del sistema.',
        icon: Store,
        pasos: [
          'Crea una sucursal indicando nombre, dirección, teléfono y email de contacto.',
          'Define correctamente cada sede para imputar ventas, compras, stock y gastos.',
          'Usa la selección de sucursal activa en la barra superior para trabajar sobre el sitio correcto.',
          'Mantén esta estructura ordenada antes de operar con inventario, caja o reportes.'
        ]
      },
      {
        id: 'permisos',
        requiredModule: 'permisos',
        title: 'Usuarios y Roles',
        description:
          'Gestión de accesos, perfiles y permisos granulares para adaptar el sistema a la estructura del equipo.',
        icon: UserCheck,
        pasos: [
          'Crea un usuario cargando nombre, email y contraseña inicial.',
          'Asigna un rol predefinido o uno personalizado según la tarea del puesto.',
          'Activa o desactiva módulos para limitar qué puede ver y operar cada persona.',
          'Usa roles personalizados cuando el negocio requiera permisos muy específicos.'
        ]
      },
      {
        id: 'importaciones',
        requiredModule: 'importaciones',
        title: 'Importaciones',
        description:
          'Carga masiva de productos, clientes, proveedores, listas de precios o stock desde Excel o CSV.',
        icon: Upload,
        pasos: [
          'Descarga la plantilla desde el módulo antes de preparar la importación.',
          'Completa el archivo respetando el formato pedido por el sistema.',
          'Sube la planilla y revisa el preview con errores o advertencias.',
          'Corrige lo necesario y confirma. Haz una exportación previa antes de cargas grandes.'
        ]
      },
      {
        id: 'config-reportes',
        requiredModule: 'config-reportes',
        title: 'Impresión y Tickets',
        description:
          'Ajuste del formato de comprobantes, tickets e impresiones para que salgan con la estructura adecuada.',
        icon: Printer,
        pasos: [
          'Define el formato de impresión que usarás en tickets y comprobantes.',
          'Ajusta qué datos deben mostrarse en cada salida impresa.',
          'Valida el resultado con pruebas antes de usarlo en operación diaria.',
          'Mantén estos parámetros coordinados con Mi Empresa y Facturación.'
        ]
      },
      {
        id: 'logs',
        requiredModule: 'logs',
        title: 'Logs de Sistema',
        description:
          'Auditoría de acciones relevantes del sistema, con registro de usuario, fecha, entidad afectada y cambios realizados.',
        icon: Database,
        pasos: [
          'Consulta los logs para revisar altas, modificaciones y bajas realizadas en el sistema.',
          'Filtra por módulo o entidad cuando necesites investigar una operación.',
          'Usa los valores anteriores y nuevos para entender qué cambió exactamente.',
          'Recuerda que estos registros son de solo lectura y forman parte de la trazabilidad de auditoría.'
        ]
      }
    ]
  }
];

export const todosLosModulos = academiaCategorias.flatMap((categoria) => categoria.modulos);
